// Helper functions
function getPropertyValues(state, property = null) {
  if (!property) {
    return []
  }
  return state?.[property]?.split(' ') || []
}

function updateProperty(state, propertyValues, property = null) {
  if (!property || !state || !Array.isArray(propertyValues)) {
    return false
  }
  state.update({ [property]: propertyValues.join(' ') })
  return true
}

function getSpacingValues(context) {
  return context?.designSystem?.spacing?.sequence || {}
}

function validateIndex(index, array) {
  return index >= 0 && index < array.length
}

function updatePropertyValuesAtIndex(state, property, index, value, syncFn) {
  const propertyValues = getPropertyValues(state, property)
  while (propertyValues.length <= index) {
    propertyValues.push('-')
  }
  propertyValues[index] = value

  if (updateProperty(state, propertyValues, property)) {
    syncFn()
  }
}

// Value conversion helpers
function findClosestSpacingKey(value, spacingValues, property = null) {
  if (typeof value !== 'number') {
    return null
  }

  // Handle negative values for margin
  const isNegative = value < 0
  const absValue = Math.abs(value)

  let closestKey = null
  let closestDiff = Infinity

  Object.entries(spacingValues).forEach(([key, val]) => {
    const diff = Math.abs(absValue - val.val)
    if (diff < closestDiff) {
      closestDiff = diff
      closestKey = key
    }
  })

  return property === 'margin' && isNegative ? `-${closestKey}` : closestKey
}

// State getters
export function getNumberValueFromState(index, property = null) {
  if (property === 'lineHeight') {
    return Math.abs(this.state?.[property])
  }

  const propertyValues = getPropertyValues(this.state, property)
  if (!validateIndex(index, propertyValues)) {
    return 0
  }

  const value = propertyValues[index]
  const spacingValues = getSpacingValues(this.context)

  return spacingValues[value]?.val || 'none'
}

export function getLetterValueFromState(index, property = null) {
  const propertyValues = getPropertyValues(this.state, property)
  if (!validateIndex(index, propertyValues)) {
    return 0
  }
  return propertyValues[index]
}

// UI sync helpers
function syncNumberInput(element, value) {
  const numberInput = element?.parent?.NumberValue?.node
  if (numberInput) {
    numberInput.value = value
  }
}

function syncLetterInput(element, value) {
  const letterInput = element?.parent?.LetterValue?.node
  if (letterInput) {
    letterInput.value = value
  }
}

// State updaters
export function updateLetterStateValue(index, property = null) {
  const value = this.node?.value?.trim()?.toUpperCase()
  const spacingValues = getSpacingValues(this.context)

  // Handle negative values
  const isNegative = property === 'margin' && value?.startsWith('-')
  const letterValue = isNegative ? value.substring(1) : value

  // Validation
  if (!letterValue || !spacingValues[letterValue]) {
    return null
  }

  // Get numeric value
  const numericValue = spacingValues[letterValue].val
  const finalNumericValue = isNegative ? -numericValue : numericValue

  // Update state
  const propertyValues = getPropertyValues(this.state, property)
  while (propertyValues.length <= index) {
    propertyValues.push('-')
  }
  propertyValues[index] = value

  // Update UI if state update successful
  if (updateProperty(this.state, propertyValues, property)) {
    syncNumberInput(this, finalNumericValue)
  }
}

export function updateNumberStateValue(index, property = null) {
  const value = this.node?.value?.trim()

  if (property === 'lineHeight') {
    this.state.update({ lineHeight: Math.abs(parseFloat(value)) || 100 })
    return
  }

  if (
    ['minWidth', 'minHeight', 'maxWidth', 'maxHeight'].includes(property) &&
    value === 'none'
  ) {
    updatePropertyValuesAtIndex(this.state, property, index, 'none', () =>
      syncLetterInput(this, '')
    )
    return
  }

  if ((property === 'width' || property === 'height') && value === 'auto') {
    updatePropertyValuesAtIndex(this.state, property, index, 'auto', () =>
      syncLetterInput(this, '')
    )
    return
  }

  // Handle special cases for 0 or '-'
  if (value === '0' || value === '-') {
    updatePropertyValuesAtIndex(this.state, property, index, '-', () =>
      syncLetterInput(this, '-')
    )
    return
  }

  const numValue = parseFloat(value)
  if (isNaN(numValue)) {
    return null
  }

  const spacingValues = getSpacingValues(this.context)
  const closestKey = findClosestSpacingKey(numValue, spacingValues, property)
  if (!closestKey) {
    return null
  }

  updatePropertyValuesAtIndex(this.state, property, index, closestKey, () =>
    syncLetterInput(this, closestKey)
  )
}

export function handleAtKey(key) {
  if (!this.call('isString', key)) {
    return ''
  }

  return key.startsWith('@') ? key : `@${key}`
}

export async function createSpacing() {
  if (!this.node.elements) {
    console.warn('createSpacing must be used inside a Form')
    return
  }
  const cleanedKey = this.call(
    'handleAtKey',
    this.node.elements.item('sidebar-new-item').value
  )

  await this.updateDesignSystem(`spacing.${cleanedKey}`, { ratio: 1.618 })
}
